import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/hooks/useLanguage";
import { supabase } from "@/integrations/supabase/client";
import { solveProblem, RateLimitError, type SolutionData } from "@/lib/solve";
import { generatePractice } from "@/lib/practice";
import { SolutionView, type SolutionStep } from "@/components/SolutionView";
import { FloatingStreak } from "@/components/FloatingStreak";
import { FloatingPro } from "@/components/FloatingPro";
import { RateLimitBanner } from "@/components/RateLimitBanner";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Dumbbell, Sparkles, RefreshCw, ChevronRight } from "lucide-react";

export const Route = createFileRoute("/practice")({
  component: PracticePage,
  head: () => ({
    meta: [
      { title: "Practice Mode - Inquira" },
      { name: "description", content: "Practice similar problems to reinforce your learning." },
      { property: "og:title", content: "Practice Mode - Inquira" },
      { property: "og:description", content: "AI-generated practice problems tailored to concepts you've already solved." },
      { property: "og:url", content: "https://www.inquira.online/practice" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://www.inquira.online/practice" }],
  }),
});

interface RecentConcept {
  concept: string;
  count: number;
  lastQuestion: string;
}

function PracticePage() {
  const { user, isLoading: authLoading, isPaid, streak } = useAuth();
  const { language } = useLanguage();
  const [concepts, setConcepts] = useState<RecentConcept[]>([]);
  const [loading, setLoading] = useState(true);
  const [generatingFor, setGeneratingFor] = useState<string | null>(null);
  const [practiceQuestion, setPracticeQuestion] = useState<string | null>(null);
  const [solving, setSolving] = useState(false);
  const [solution, setSolution] = useState<SolutionData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rateLimit, setRateLimit] = useState<{ retryAfter: number; message: string } | null>(null);

  const lastAction = { current: null as null | (() => Promise<void>) };

  useEffect(() => {
    if (!user) return;
    const fetch = async () => {
      const { data } = await supabase
        .from("solved_problems")
        .select("concept, question")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(50);

      if (data) {
        const map = new Map<string, { count: number; lastQuestion: string }>();
        for (const row of data) {
          const c = row.concept || "General";
          const existing = map.get(c);
          if (existing) {
            existing.count++;
          } else {
            map.set(c, { count: 1, lastQuestion: row.question });
          }
        }
        setConcepts(
          Array.from(map.entries()).map(([concept, v]) => ({
            concept,
            count: v.count,
            lastQuestion: v.lastQuestion,
          }))
        );
      }
      setLoading(false);
    };
    fetch();
  }, [user]);

  const handleGenerate = async (concept: string, exampleQuestion: string) => {
    setGeneratingFor(concept);
    setError(null);
    setRateLimit(null);
    setPracticeQuestion(null);
    setSolution(null);

    try {
      const question = await generatePractice(concept, exampleQuestion, language.code);
      setPracticeQuestion(question);
    } catch (err) {
      if (err instanceof RateLimitError) {
        setRateLimit({ retryAfter: err.retryAfterSeconds, message: err.message });
        lastAction.current = () => handleGenerate(concept, exampleQuestion);
      } else {
        setError(err instanceof Error ? err.message : "Failed to generate practice problem");
      }
    } finally {
      setGeneratingFor(null);
    }
  };

  const handleSolve = async () => {
    if (!practiceQuestion) return;
    setSolving(true);
    setError(null);
    setRateLimit(null);
    try {
      const result = await solveProblem(practiceQuestion, "text", language.code);
      setSolution(result);
    } catch (err) {
      if (err instanceof RateLimitError) {
        setRateLimit({ retryAfter: err.retryAfterSeconds, message: err.message });
        lastAction.current = () => handleSolve();
      } else {
        setError(err instanceof Error ? err.message : "Failed to solve");
      }
    } finally {
      setSolving(false);
    }
  };

  if (authLoading) return null;

  if (!user) {
    return (
      <div className="min-h-screen notebook-lines flex items-center justify-center px-4">
        <div className="text-center space-y-4">
          <Dumbbell className="size-12 text-ink-muted mx-auto" />
          <p className="text-ink-muted">Sign in to practice</p>
          <Link to="/login" className="text-neon-pink font-semibold">Sign In</Link>
        </div>
      </div>
    );
  }

  if (!isPaid) {
    return (
      <div className="min-h-screen notebook-lines flex items-center justify-center px-4">
        <div className="text-center space-y-4 max-w-sm">
          <div className="size-16 mx-auto rounded-full bg-neon-pink/10 flex items-center justify-center">
            <Dumbbell className="size-8 text-neon-pink" />
          </div>
          <h2 className="font-[family-name:var(--font-display)] font-extrabold text-xl text-ink">
            Practice Mode is Pro Only
          </h2>
          <p className="text-sm text-ink-muted">
            Premium includes practice problems within your daily AI question allowance.
          </p>
          <Link to="/upgrade">
            <Button variant="scan" size="full" className="font-[family-name:var(--font-display)]">
              <Sparkles className="size-5" /> Upgrade to Pro
            </Button>
          </Link>
          <Link to="/" className="block text-xs text-ink-muted hover:text-ink transition-colors">
            ← Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen notebook-lines">
      <div className="max-w-lg mx-auto px-4 pb-24">
        <FloatingStreak streak={streak} />
        <FloatingPro isPaid={isPaid} />
        <header className="flex items-center gap-3 py-4">
          <Link to="/" aria-label="Back to home" title="Back to home" className="size-9 rounded-full bg-card border border-rule flex items-center justify-center hover:bg-rule/30 transition-colors">
            <ArrowLeft className="size-4 text-ink-muted" />
          </Link>
          <h1 className="font-[family-name:var(--font-display)] font-extrabold text-xl text-ink">
            Practice Mode
          </h1>
          <Dumbbell className="size-5 text-neon-pink ml-auto" />
        </header>

        {rateLimit && (
          <div className="mb-4">
            <RateLimitBanner
              retryAfterSeconds={rateLimit.retryAfter}
              message={rateLimit.message}
              onRetry={() => {
                const fn = lastAction.current;
                setRateLimit(null);
                fn?.();
              }}
              onDismiss={() => setRateLimit(null)}
            />
          </div>
        )}

        {/* Generated practice problem */}
        {practiceQuestion && (
          <div className="space-y-4 mb-6">
            <div className="bg-card rounded-2xl p-5 border border-neon-blue/30 shadow-sm space-y-3">
              <p className="text-xs font-bold uppercase tracking-widest text-neon-blue">Practice Problem</p>
              <p className="text-sm font-[family-name:var(--font-mono)] text-ink leading-relaxed">
                {practiceQuestion}
              </p>
              <div className="flex gap-2">
                {!solution && (
                  <Button
                    variant="scan"
                    size="sm"
                    onClick={handleSolve}
                    disabled={solving}
                    className="flex-1"
                  >
                    {solving ? (
                      <><Sparkles className="size-4 animate-pulse" /> Solving...</>
                    ) : (
                      <><Sparkles className="size-4" /> Show Solution</>
                    )}
                  </Button>
                )}
                <Button
                  variant="subtle"
                  size="sm"
                  onClick={() => { setPracticeQuestion(null); setSolution(null); }}
                >
                  <RefreshCw className="size-4" /> New
                </Button>
              </div>
            </div>

            {solution && (
              <SolutionView
                concept={solution.concept}
                conceptDescription={solution.conceptDescription}
                steps={solution.steps as SolutionStep[]}
                finalAnswer={solution.finalAnswer}
                summary={solution.summary}
                onPracticeSimilar={() => { setPracticeQuestion(null); setSolution(null); }}
              />
            )}

            {solution && /calculus|derivative|integral|limit|series|taylor/i.test(
              `${solution.concept} ${practiceQuestion ?? ""}`,
            ) && (
              <div className="rounded-2xl border border-rule bg-card p-4 text-sm text-ink-muted">
                Struggling with this topic?{" "}
                <Link to="/calculus-ai-solver" className="text-neon-pink font-semibold underline">
                  Read the Calculus AI Solver guide →
                </Link>
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="bg-destructive/10 text-destructive text-sm rounded-xl p-3 border border-destructive/20 text-center mb-4">
            {error}
          </div>
        )}

        {/* Concept list */}
        {!practiceQuestion && (
          <>
            <p className="text-sm text-ink-muted mb-4">
              Choose a topic to generate a similar practice problem based on what you've studied.
            </p>

            {loading ? (
              <div className="flex flex-col items-center gap-4 py-12">
                <div className="size-12 rounded-full bg-neon-pink/10 flex items-center justify-center animate-pulse">
                  <Sparkles className="size-6 text-neon-pink" />
                </div>
                <p className="text-sm text-ink-muted">Loading your topics...</p>
              </div>
            ) : concepts.length === 0 ? (
              <div className="text-center py-12 space-y-3">
                <Dumbbell className="size-12 text-ink-muted/30 mx-auto" />
                <p className="text-ink-muted text-sm">Solve some problems first to unlock practice mode</p>
                <Link to="/" className="text-neon-pink text-sm font-semibold">Solve a problem →</Link>
              </div>
            ) : (
              <div className="space-y-3">
                {concepts.map((c) => (
                  <button
                    key={c.concept}
                    onClick={() => handleGenerate(c.concept, c.lastQuestion)}
                    disabled={generatingFor === c.concept}
                    className="w-full text-left bg-card rounded-2xl p-4 border border-rule shadow-sm hover:border-neon-pink/30 transition-colors disabled:opacity-60"
                  >
                    <div className="flex items-center gap-3">
                      <div className="size-10 rounded-full bg-neon-pink/10 flex items-center justify-center shrink-0">
                        {generatingFor === c.concept ? (
                          <Sparkles className="size-5 text-neon-pink animate-pulse" />
                        ) : (
                          <Dumbbell className="size-5 text-neon-pink" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-ink">{c.concept}</p>
                        <p className="text-xs text-ink-muted mt-0.5">{c.count} problem{c.count !== 1 ? "s" : ""} solved</p>
                      </div>
                      <ChevronRight className="size-4 text-ink-muted shrink-0" />
                    </div>
                  </button>
                ))}
              </div>
            )}
            {!loading && concepts.some((c) => /calculus|derivative|integral|limit|series|taylor/i.test(c.concept)) && (
              <p className="text-xs text-ink-muted mt-4 text-center">
                New to calculus?{" "}
                <Link to="/calculus-ai-solver" className="text-neon-pink font-semibold underline">
                  See our step-by-step guide
                </Link>
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
